import React, { useState, useRef, useEffect } from 'react';

export default function ContratistaSelector({ contratistas, value, onChange, label = 'Contratista' }) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const selectedContratista = contratistas.find(c => c.id === value);

    const filteredOptions = contratistas.filter(c =>
        c.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const handleSelect = (id) => {
        onChange(id);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setSearchTerm('');
    };

    const styles = {
        wrapper: {
            position: 'relative',
            width: '100%',
            maxWidth: '360px',
            fontFamily: 'var(--font-family, inherit)',
        },
        label: {
            display: 'block',
            fontSize: '0.8rem',
            fontWeight: '600',
            color: 'var(--text-muted, #64748b)',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
        },
        trigger: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            backgroundColor: '#fff',
            border: '1.5px solid #cbd5e1',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: isOpen ? '0 0 0 3px rgba(99, 102, 241, 0.15)' : '0 1px 2px rgba(0,0,0,0.04)',
            borderColor: isOpen ? 'var(--primary, #6366f1)' : '#cbd5e1',
            minHeight: '44px',
        },
        triggerText: {
            fontSize: '0.92rem',
            color: selectedContratista ? 'var(--sidebar-bg, #0f172a)' : '#94a3b8',
            fontWeight: selectedContratista ? '600' : '400',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
        },
        clearBtn: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: 'transparent',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: '1rem',
            lineHeight: 1,
            marginRight: '6px',
            transition: 'all 0.15s',
        },
        chevron: {
            width: '18px',
            height: '18px',
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            color: 'var(--text-muted, #64748b)',
            flexShrink: 0,
        },
        dropdown: {
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
            border: '1px solid #e2e8f0',
            zIndex: 1000,
            overflow: 'hidden',
            animation: isOpen ? 'selectorSlideIn 0.18s ease' : 'none',
        },
        searchWrap: {
            padding: '10px 12px',
            borderBottom: '1px solid #f1f5f9',
        },
        searchInput: {
            width: '100%',
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1.5px solid #e2e8f0',
            fontSize: '0.85rem',
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.15s',
        },
        optionsList: {
            maxHeight: '240px',
            overflowY: 'auto',
            padding: '4px',
        },
        option: {
            padding: '10px 12px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.88rem',
            color: '#334155',
            transition: 'background-color 0.12s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        optionSelected: {
            backgroundColor: 'rgba(99, 102, 241, 0.08)',
            color: 'var(--primary, #6366f1)',
            fontWeight: '600',
        },
        optionHover: {
            backgroundColor: '#f8fafc',
        },
        checkmark: {
            fontSize: '0.9rem',
            color: 'var(--primary, #6366f1)',
        },
        emptyState: {
            padding: '20px',
            textAlign: 'center',
            color: '#94a3b8',
            fontSize: '0.85rem',
        },
    };

    return (
        <div style={styles.wrapper} ref={containerRef}>
            {label && <label style={styles.label}>{label}</label>}
            <div
                style={styles.trigger}
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; }}
                onMouseLeave={(e) => { if (!isOpen) e.currentTarget.style.borderColor = '#cbd5e1'; }}
            >
                <span style={styles.triggerText}>
                    {selectedContratista ? selectedContratista.nombre : 'Todos los contratistas'}
                </span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {selectedContratista && (
                        <button
                            style={styles.clearBtn}
                            onClick={handleClear}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
                            title="Mostrar todos"
                        >
                            ×
                        </button>
                    )}
                    <svg style={styles.chevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </div>
            </div>

            {isOpen && (
                <div style={styles.dropdown}>
                    <div style={styles.searchWrap}>
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Buscar contratista..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                            onFocus={(e) => { e.target.style.borderColor = 'var(--primary, #6366f1)'; }}
                            onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
                        />
                    </div>
                    <div style={styles.optionsList}>
                        <div
                            style={{
                                ...styles.option,
                                ...(value === '' ? styles.optionSelected : {}),
                            }}
                            onClick={() => handleSelect('')}
                            onMouseEnter={(e) => { if (value !== '') e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = value === '' ? 'rgba(99, 102, 241, 0.08)' : 'transparent'; }}
                        >
                            <span>Todos los contratistas</span>
                            {value === '' && <span style={styles.checkmark}>✓</span>}
                        </div>
                        {filteredOptions.map(c => (
                            <div
                                key={c.id}
                                style={{
                                    ...styles.option,
                                    ...(value === c.id ? styles.optionSelected : {}),
                                }}
                                onClick={() => handleSelect(c.id)}
                                onMouseEnter={(e) => { if (value !== c.id) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = value === c.id ? 'rgba(99, 102, 241, 0.08)' : 'transparent'; }}
                            >
                                <span>{c.nombre}</span>
                                {value === c.id && <span style={styles.checkmark}>✓</span>}
                            </div>
                        ))}
                        {filteredOptions.length === 0 && (
                            <div style={styles.emptyState}>No se encontraron contratistas</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
